const States = {
  ORIGINAL_BASE: 0,
  ORIGINAL_SCALED: 1,
  ORIGINAL_PIXELATED: 2,
  REMIXED_READY: 3,
  REMIXED_UNPIXELATED: 4,
  REMIXED_UNSCALED: 5,
};

export default class RemixImageManager {
  constructor() {
    this.paintingContainerEl = document.getElementById("painting-container");
    this.paintingEl = document.getElementById("painting");
    this.imageEl = document.getElementById("painting-img");

    // Pixelation animation
    this.pixelationFactor = 40;
    this.pixelationFactorCurrent = 1;
    this.timeBetweenTicksDefault = 66; // GOOD is 66
    this.timeBetweenTicks = this.timeBetweenTicksDefault;
    this.timeBetweenTicksMax = 200;
    this.newTick = false;

    this.isPixelating = false;
    this.startTime = null;
    this.previousDate = null;

    this.canvasEl = document.getElementById("painting-canvas");
    this.context = this.canvasEl.getContext("2d");
    this.canvasHeight = 0;
    this.canvasWidth = 0;
    this.imageToPixelate = new Image();
    this.imageToPixelate.crossOrigin = "Anonymous";
    this.originalImageData = null;
    this.imageToPixelateHeight = 0;
    this.imageToPixelateWidth = 0;
    this.savedPixels = [];
    this.currentScaleHeight = 0;
    this.currentScaleWidth = 0;
    this.state = States.ORIGINAL_BASE;
    this.pixelationSteps = [
      1, 8, 14, 19, 23, 26, 28, 30, 32, 33, 34, 34, 36, 40,
    ];

    window.addEventListener('resize', this.onResize.bind(this));
  }

  pixelateImage() {
    if (this.state !== States.ORIGINAL_BASE) {
      console.log("An image is already animating");
      return;
    }

    this.canvasEl.classList.add('remixing');

    this.imageToPixelate.onload = () => {
      this.onResize();
      this.imageToPixelateHeight = this.imageToPixelate.height;
      this.imageToPixelateWidth = this.imageToPixelate.width;
      this.pixelate();
    };

    const src =
      this.imageEl.getAttribute("data-src") ||
      this.imageEl.style.backgroundImage.slice(4, -1).replace(/"/g, "");
    this.imageToPixelate.src = src;
  }

  updateSrc(src) {
    this.imageEl.style.backgroundImage = `url('${src}')`;
  }

  unpixelateImage(src) {
    this.isPixelating = true;
    if (this.state === States.ORIGINAL_BASE) {
      return;
    }

    this.reverse = true;

    this.imageToPixelate.onload = this.onRemixedImageLoad.bind(this);

    if (src) {
      this.imageToPixelate.src = src;
    } else {
      this.onRemixedImageLoad();
    }
  }

  onRemixedImageLoad() {
    this.state = States.REMIXED_READY;
    this.imageToPixelateHeight = this.imageToPixelate.height;
    this.imageToPixelateWidth = this.imageToPixelate.width;
    this.timeBetweenTicks = this.timeBetweenTicksDefault;

    this.pixelate();
  }

  pixelate() {
    if (this.isPixelating) {
      return;
    }
    this.canvasEl.height = this.canvasHeight;
    this.canvasEl.width = this.canvasWidth;

    this.isPixelating = true;
    this.previousDate = new Date().getTime();

    window.requestAnimationFrame(this.pixelateInRaf.bind(this));
  }

  pixelateInRaf() {
    if (!this.isPixelating) {
      return;
    }

    const newDate = new Date().getTime();
    if (newDate - this.previousDate > this.timeBetweenTicks) {
      this.previousDate = newDate;
      this.newTick = true;
    }

    if (
      this.state === States.ORIGINAL_BASE
    ) {
      this.scaleImage();
    } else if (
      this.state === States.REMIXED_UNPIXELATED
    ) {
      this.reset();
    } else if (
      this.state === States.ORIGINAL_SCALED ||
      this.state === States.ORIGINAL_PIXELATED ||
      this.state === States.REMIXED_READY
    ) {
      if (
        this.state === States.REMIXED_READY &&
        this.pixelationFactorCurrent === this.pixelationFactor
      ) {
        // Draw the new image to get the image data to pixelate it.
        this.scaleImage();

        // To force the first pixelation draw.
        this.newTick = true;
      }

      this.drawPixelatedImage();
    }

    this.newTick = false;
    if (!this.isPixelating) {
      return;
    }
    window.requestAnimationFrame(this.pixelateInRaf.bind(this));
  }

  scaleImage() {    
    const imageRatio = this.imageToPixelateHeight / this.imageToPixelateWidth;
    const canvasRatio = this.canvasHeight / this.canvasWidth;
    let newHeight = this.canvasHeight;
    let newWidth = this.canvasWidth;
    let scaledHeight = this.canvasHeight;
    let scaledWidth = this.canvasWidth;

    if (imageRatio > canvasRatio) {
      newWidth = this.canvasHeight / imageRatio;
      scaledHeight = this.canvasWidth * imageRatio;
    } else {
      newHeight = this.canvasWidth * imageRatio;
      scaledWidth = this.canvasHeight / imageRatio;
    }

    let isScaled = false;
    if (
      this.state === States.ORIGINAL_BASE ||
      this.state === States.REMIXED_READY
    ) {
      if (this.currentScaleHeight === 0) {
        this.currentScaleHeight = newHeight;
        this.currentScaleWidth = newWidth;
      }

      const easing = 0.1;
      this.currentScaleHeight +=
        (scaledHeight - this.currentScaleHeight) * easing;
      this.currentScaleWidth += (scaledWidth - this.currentScaleWidth) * easing;

      if (Math.abs(this.currentScaleHeight - scaledHeight) < 5) {
        this.currentScaleHeight = scaledHeight;
        this.currentScaleWidth = scaledWidth;
        isScaled = true;
      }
    } else if (this.state === States.REMIXED_UNPIXELATED) {
      const easing = 0.1;
      // Works if remixed image is squared. To update if it changes.
      const targetHeight = Math.min(this.canvasHeight, this.canvasWidth);
      const targetWidth = Math.min(this.canvasHeight, this.canvasWidth);
      this.currentScaleHeight +=
        (targetHeight - this.currentScaleHeight) * easing;
      this.currentScaleWidth += (targetWidth - this.currentScaleWidth) * easing;

      if (
        Math.abs(this.currentScaleHeight - targetHeight) < 1 &&
        Math.abs(this.currentScaleWidth - targetWidth) < 0.1
      ) {
        // Ending unpixelate animation
        this.reset();
      }
    }

    const offsetX = (this.canvasWidth - this.currentScaleWidth) * 0.5;
    const offsetY = (this.canvasHeight - this.currentScaleHeight) * 0.5;

    this.context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    this.context.drawImage(
      this.imageToPixelate,
      0,
      0,
      this.imageToPixelateWidth,
      this.imageToPixelateHeight,
      offsetX,
      offsetY,
      this.currentScaleWidth,
      this.currentScaleHeight
    );

    if (isScaled || this.state === States.REMIXED_READY) {
      if (this.state !== States.REMIXED_READY) {
        this.state = States.ORIGINAL_SCALED;
      }
      this.originalImageData = this.context.getImageData(
        0,
        0,
        this.canvasWidth,
        this.canvasHeight
      ).data;
    }
  }

  drawPixelatedImage() {
    if (!this.newTick) {
      return;
    }

    const pixelationDirection = this.state === States.REMIXED_READY ? -1 : 1;
    const newPixelationFactor =
      this.pixelationSteps[
        this.pixelationSteps.indexOf(this.pixelationFactorCurrent) +
          pixelationDirection
      ] || (this.state === States.REMIXED_READY ? 0 : this.pixelationFactor);

    if (newPixelationFactor == 1 && this.state === States.REMIXED_READY) {
      this.state = States.REMIXED_UNPIXELATED;
      return;
    }
    
    this.context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

    if (newPixelationFactor === this.pixelationFactorCurrent && this.state !== States.REMIXED_UNPIXELATED) {
      this.pixelationFactorCurrent = this.pixelationFactor;
      this.timeBetweenTicks = Math.min(
        this.timeBetweenTicks * 1.2,
        this.timeBetweenTicksMax
      );
    } else if (newPixelationFactor === 1 && this.state === States.REMIXED_READY) {
      this.timeBetweenTicks === 0;
    } else {
      this.pixelationFactorCurrent = newPixelationFactor;
    }

    let shouldSavePixels =
      this.pixelationFactorCurrent === this.pixelationFactor &&
      this.savedPixels.length === 0;
    let savedPixelsCopy = [...this.savedPixels];

    if (this.state === States.REMIXED_READY) {
      savedPixelsCopy = [];
      shouldSavePixels = false;
    }

    for (let y = 0; y < this.canvasHeight; y += this.pixelationFactorCurrent) {
      for (let x = 0; x < this.canvasWidth; x += this.pixelationFactorCurrent) {
        // extracting the position of the sample pixel
        const pixelIndexPosition = (x + y * this.canvasWidth) * 4;
        // drawing a square replacing the current pixels
        let pixel = null;
        const realY = y / this.pixelationFactorCurrent;
        const realX = x / this.pixelationFactorCurrent;

        if (savedPixelsCopy.length > 0) {
          const newY = Math.min(
            savedPixelsCopy.length - 1,
            Math.max(0, realY + [-1, 0, 1][parseInt(Math.random() * 3)])
          );
          const newX = Math.min(
            savedPixelsCopy[0].length - 1,
            Math.max(0, realX + [-1, 0, 1][parseInt(Math.random() * 3)])
          );

          pixel = savedPixelsCopy[newY][newX];
        } else {
          pixel = `rgba(
            ${this.originalImageData[pixelIndexPosition]},
            ${this.originalImageData[pixelIndexPosition + 1]},
            ${this.originalImageData[pixelIndexPosition + 2]},
            ${this.originalImageData[pixelIndexPosition + 3]}
          )`;
        }
        this.context.fillStyle = pixel;

        if (shouldSavePixels) {
          if (!this.savedPixels[realY]) {
            this.savedPixels.push([]);
          }
          this.savedPixels[realY].push(pixel);
        }

        this.context.fillRect(
          x,
          y,
          this.pixelationFactorCurrent,
          this.pixelationFactorCurrent
        );
      }
    }

    if (shouldSavePixels) {
      this.state = States.ORIGINAL_PIXELATED;
    }
  }

  reset() {
    this.isPixelating = false;
    this.updateSrc(this.imageToPixelate.src);
    this.state = States.ORIGINAL_BASE;
    this.originalImageData = null;

    this.canvasHeight = 0;
    this.canvasWidth = 0;
    this.imageToPixelateHeight = 0;
    this.imageToPixelateWidth = 0;
    this.savedPixels = [];
    this.currentScaleHeight = 0;
    this.currentScaleWidth = 0;

    this.canvasEl.classList.remove('remixing');
  }

  onResize() {
    const paintingContainerBounds =
      this.paintingContainerEl.getBoundingClientRect();
    this.canvasHeight = parseInt(paintingContainerBounds.height);
    this.canvasWidth = parseInt(paintingContainerBounds.width);

    this.canvasEl.height = this.canvasHeight;
    this.canvasEl.width = this.canvasWidth;
  }
}
